import {Injectable} from '@angular/core';

import {ProgressNavigationItem} from '@wba/models/progress-navigation-item';
import {ProgressNavigationListV2} from '@wba/models/progress-navigation-list';
import {ProgressNavItemsEnum} from '@wba/models/enums/progress-nav-items.enum';
import {NavigationEnd, Router} from '@angular/router';
import {ProgressStatusEnum} from '@wba/models/enums/progress-status.enum';
import {Title} from '@angular/platform-browser';
import {Subject} from 'rxjs';


@Injectable({
  providedIn: 'root'
})
export class ProgressNavigationService {
  public changeValues: Subject<ProgressNavigationItem[]> = new Subject();
  public navigate: Subject<string[]> = new Subject();
  public dirty: boolean;
  private _items: ProgressNavigationItem[]
  private _requiredItemIsDone = false;

  constructor(private readonly router: Router, private readonly titleService: Title) {

    this._items = ProgressNavigationListV2;

    this.restore(router.url)

    this.router.events.subscribe(val => {
      if (val instanceof NavigationEnd) {
        const url = val.url.substring(0, val.url.indexOf('?'));
        const foundPage = this.getItems().find(item => url.includes(item.url));
        if (foundPage) {
          this.setStatusActive(foundPage.id);
          this.titleService.setTitle(`Weiterbewilligungsantrag: ${foundPage.label} - jobcenter.digital`);
        }
      }
    })
  }

  getItems() {
    return this._items;
  }

  setItems(items: ProgressNavigationItem[]) {
    this._items = items;
  }

  /**
   * Restore items for a given (partial, only path+query) URL
   *
   * Known problem: previous page visibility cannot be restored from here
   * without loading the BedarfsgemeinschaftComponent.
   */
  restore(url: string) {

    const path = url.substring(0, url.indexOf('?'));
    const foundPage = this.getItems()
      .find(item => path.includes(item.url));

    if(foundPage != undefined) {
      // current page has to be visible, otherwise should be redirected by routing?
      foundPage.visible = true

      /*
        assuming all pages we already progressed past should be DONE
        otherwise, state would have to be saved
       */
      for(let page of this._items) {
        if(page.id == foundPage.id) break;
        page.status = ProgressStatusEnum.DONE
      }

      this.setStatusActive(foundPage.id)
    }
  }

  getItem(index): ProgressNavigationItem {
    if (this.getItems().find(item => item.id === index)) {
      return this.getItems()[index];
    }
    return null;
  }

  /**
   * um die Item-Felder ins JSON-Format zu bringen, braucht man ein Mapping, bei dem alle Feldernamen die Attributnamen haben.
   * Es wird kein Mapping auf andere Namen oder bei den Werten benötigt.
   */
  getItemsWithMetaData() {
    return this._items.map(item => ({id: item.id, progressed: item.progressed, disabled: item.disabled, status: item.status, visible: item.visible}));
  }

  jumpToPage(pageIndex) {
    this.dirty = true;
    this.navigate.next([this.getItem(pageIndex).url]);
    this.titleService.setTitle(this.getItem(pageIndex).label);
  }

  nextPage(currentPageIndex: number, currentStatus?: ProgressStatusEnum) {
    const nextPageIndex = this.getItems().slice(currentPageIndex + 1).find(item => item.visible);
    this.setStatus(currentPageIndex, currentStatus, false);
    this.jumpToPage(nextPageIndex.id);
    this.titleService.setTitle(nextPageIndex.label);
  }

  prevPage(currentPageIndex: number, currentStatus?: ProgressStatusEnum) {
    const prevPageIndex = this._items.slice(0, currentPageIndex).reverse().find(f => f.visible);
    this.setStatus(currentPageIndex, currentStatus, false);
    this.jumpToPage(prevPageIndex.id);
  }

  setStatus(pageIndex, status: ProgressStatusEnum, triggerChangeValues = true) {
    if (typeof status !== 'undefined') {
      this.getItem(pageIndex).status = status;
      this.dirty = true;
      if (triggerChangeValues) {
        this.changeValues.next(this.getItems());
      }
    }
  }

  setVisible(pageIndex, visible, triggerChangeValues = true) {
    if (typeof visible !== 'undefined') {
      this.getItem(pageIndex).visible = visible;
      this.dirty = true;
      if (triggerChangeValues) {
        this.changeValues.next(this.getItems());
      }
    }
  }

  // private methods
  private setStatusActive(pageIndex) {
    this._requiredItemIsDone = !!this.getItems().find(item => item.id === ProgressNavItemsEnum.BG);
    this.getItems().forEach(item => {
      item.active = item.id === pageIndex;
      item.progressed = item.id <= pageIndex;
      item.disabled = !item.active && !this._requiredItemIsDone;
    });
    this.changeValues.next(this.getItems());
  }
}
